import { Component, computed, inject, input, output, OutputEmitterRef, signal, Type } from '@angular/core';
import { take, catchError, filter, delay } from 'rxjs';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MDDropDownComponent, MDAlertService, MDDialogService, IAlert } from '@melodic-ui/components';
import { LessonService } from '@lb/api/mylb/services/lesson.service';
import { LessonApiService } from '@lb/api/lesson/services/lesson-api.service';
import { LessonMetadataService } from '@lb/services';
import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { canTakeAttendance, isLessonEditable } from '@lb/components/lessons/utils/lesson-helper.util';
import { AttendanceDialogComponent } from '../attendance-dialog/attendance-dialog.component';

@Component({
	selector: 'lb-lesson-dropdowns',
	standalone: true,
	imports: [MDDropDownComponent],
	templateUrl: './lesson-dropdowns.component.html',
	styleUrl: './lesson-dropdowns.component.scss'
})
export class LessonDropdownsComponent {
	private _lessonService = inject(LessonService);
	private _lessonApiService = inject(LessonApiService);
	private _lessonMetadataService = inject(LessonMetadataService);
	private _alertService = inject(MDAlertService);
	private _dialogService = inject(MDDialogService);
	private _currentTime = signal(new Date());

	public lesson = input.required<ILessonListItem>();
	public loading = input<boolean>(false);
	public showAttendance = input<boolean>(true);

	public dataChanged: OutputEmitterRef<void> = output();

	// Internal state
	private _updating = signal<boolean>(false);
	private _updatingAttendance = signal<boolean>(false);
	private _initializing = signal<boolean>(true);

	public attendanceDisabled = computed(() => this.dropdownsDisabled() || !this.canTakeAttendance() || this._updatingAttendance());

	public lessonTypeOptions = computed(() => {
		return this._lessonMetadataService.getLessonTypeOptions(this.lesson().programID)();
	});

	public attendanceStatusOptions = computed(() => {
		return this._lessonMetadataService.getAttendanceStatusOptions(this.lesson().programID)();
	});

	public dropdownsReady = computed(() => {
		const typesReady = this._lessonMetadataService.typesLoadedForProgram(this.lesson().programID);
		const statusesReady = this._lessonMetadataService.statusesLoadedForProgram(this.lesson().programID);
		const hasTypeOptions = this.lessonTypeOptions().length > 0;
		const hasAttendanceOptions = this.attendanceStatusOptions().length > 0;

		return typesReady && statusesReady && hasTypeOptions && hasAttendanceOptions;
	});

	// Also disabled on a lesson from another instructor's era (predecessor or substitute): view-only.
	public dropdownsDisabled = computed(
		() =>
			this.loading() ||
			this._updating() ||
			!isLessonEditable(this.lesson()) ||
			this.lesson().lessonStatusID === 3 ||
			this.lesson().lessonStatusID === 4 ||
			this.lesson().lessonStatusID === 5
	);

	public canTakeAttendance = computed(() => canTakeAttendance(this.lesson(), this._currentTime()));

	constructor() {
		// Initialize metadata service when lesson input becomes available
		toObservable(this.lesson)
			.pipe(
				filter((lesson) => !!lesson && !this._lessonMetadataService.isLoadedForProgram(lesson.programID)()),
				takeUntilDestroyed()
			)
			.subscribe((lesson) => {
				this._lessonMetadataService.initializeForProgram(lesson.programID);
			});

		// Use observable to detect when dropdowns are ready and set initialization complete
		toObservable(this.dropdownsReady)
			.pipe(
				filter((ready) => ready && this._initializing()),
				delay(50),
				takeUntilDestroyed()
			)
			.subscribe(() => {
				this._initializing.set(false);
			});
	}

	updateType(type: unknown): void {
		const lesson = this.lesson();

		// Skip if this is an initialization event or if the value hasn't actually changed
		if (this._initializing() || !type || type === lesson.lessonTypeID || this._updating()) return;

		this._updating.set(true);

		this._lessonApiService
			.changeLessonType(lesson.lessonID, +type)
			.pipe(
				take(1),
				catchError((error) => {
					this._updating.set(false);
					if (error.error?.type === 'System.ApplicationException') {
						this._alertService.addAlert({
							type: 'error',
							title: 'Unable to Update Lesson Type',
							message: error.error.message,
							duration: 5000
						} as IAlert);
					} else {
						this._alertService.addAlert({
							type: 'error',
							title: 'Update Failed',
							message: 'Unable to update lesson type. Please try again.',
							duration: 5000
						} as IAlert);
					}
					this.dataChanged.emit();
					throw error;
				})
			)
			.subscribe(() => {
				this._updating.set(false);
				this._alertService.addAlert({
					type: 'success',
					title: 'Lesson Type Updated',
					message: 'The lesson type has been successfully updated.',
					duration: 3000
				} as IAlert);
				this.dataChanged.emit();
			});
	}

	updateAttendance(status: unknown): void {
		const currentStatusID = this.lesson().attendanceStatusID;

		if (!status || status === currentStatusID || this._updatingAttendance()) {
			return;
		}

		const dialogRef = this._dialogService.open(AttendanceDialogComponent as Type<any>, {
			lessonID: this.lesson().lessonID,
			options: this.attendanceStatusOptions(),
			currentAttendanceStatusID: status ? +status : null
		});

		dialogRef.afterClosed.pipe(take(1)).subscribe((result) => {
			if (result) {
				this.dataChanged.emit();
			}
		});
	}
}
